/**
 * ============================================================================
 * CATEGORY MAINTENANCE PAGE
 * ============================================================================
 * Settings page for managing Master Cuisines and their micro-category
 * assignments. Supports swap, reorder, edit, delete, and attach operations.
 * ============================================================================
 */

import React, { useState, useEffect, useCallback } from 'react';
import { FaArrowLeft, FaEdit, FaTrash, FaPlus, FaTimes, FaChevronDown, FaExchangeAlt, FaArrowUp, FaArrowDown, FaSave } from 'react-icons/fa';
import UnitMappingService from '../services/unitMappingService';
import './CategoryMaintenancePage.css';

const API_URL = process.env.REACT_APP_API_URL;

const CategoryMaintenancePage = ({ goTo }) => {
  const [masterCategories, setMasterCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  // Swap state
  const [swapDropdownOpen, setSwapDropdownOpen] = useState(null); // `${masterId}-${microId}`
  const [swapConfirm, setSwapConfirm] = useState(null);
  const [swapping, setSwapping] = useState(false);

  // Edit state
  const [editingMaster, setEditingMaster] = useState(null);
  const [editName, setEditName] = useState('');

  // Add custom cuisine
  const [showAddCuisine, setShowAddCuisine] = useState(false);
  const [newCuisineName, setNewCuisineName] = useState('');

  // Unmapped micro-categories (for attach feature)
  const [unmappedMicros, setUnmappedMicros] = useState([]);
  const [attachDropdownOpen, setAttachDropdownOpen] = useState(null); // master_category_id
  const [attachingTo, setAttachingTo] = useState(null);

  const businessId = UnitMappingService.getBusinessId();
  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
    'X-Business-ID': businessId
  };

  const showToast = (msg, duration = 3000) => {
    setToast(msg);
    setTimeout(() => setToast(null), duration);
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [mastersRes, unmappedRes] = await Promise.all([
        fetch(`${API_URL}/master-categories?businessId=${businessId}`, { headers }),
        fetch(`${API_URL}/master-categories/unmapped?businessId=${businessId}`, { headers })
      ]);
      const mastersData = await mastersRes.json();
      const unmappedData = await unmappedRes.json();
      if (mastersData.success) {
        setMasterCategories(mastersData.data || []);
      }
      if (unmappedData.success) {
        setUnmappedMicros(unmappedData.data || []);
      }
    } catch (err) {
      setError('Failed to load categories');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  useEffect(() => { loadData(); }, [loadData]);

  // Swap a micro-category to a different master
  const handleSwap = async (microCategoryId, toMasterCategoryId, microName, toMasterName) => {
    setSwapping(true);
    try {
      const res = await fetch(`${API_URL}/master-categories/swap`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ microCategoryId, toMasterCategoryId })
      });
      const data = await res.json();

      if (data.success) {
        showToast(`✅ Moved "${microName}" to ${toMasterName}`);
        await loadData();
      } else if (res.status === 423) {
        showToast('⏳ Category recalculation in progress. Please wait.');
      } else {
        showToast(`❌ ${data.error || 'Swap failed'}`);
      }
    } catch (err) {
      showToast('❌ Network error during swap');
    } finally {
      setSwapping(false);
      setSwapConfirm(null);
      setSwapDropdownOpen(null);
    }
  };

  // Move master up/down in display order
  const handleReorder = async (masterId, direction) => {
    const sorted = [...masterCategories].sort((a, b) => a.display_order - b.display_order);
    const idx = sorted.findIndex(m => m.master_category_id === masterId);
    if (direction === 'up' && idx <= 0) return;
    if (direction === 'down' && idx >= sorted.length - 1) return;

    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    const newOrder = sorted.map((m, i) => ({
      masterCategoryId: m.master_category_id,
      displayOrder: i === idx ? sorted[swapIdx].display_order : i === swapIdx ? sorted[idx].display_order : m.display_order
    }));

    try {
      await fetch(`${API_URL}/master-categories/reorder`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ order: newOrder })
      });
      await loadData();
    } catch (err) {
      showToast('❌ Failed to reorder');
    }
  };

  // Edit master name
  const handleEditSave = async (masterId) => {
    if (!editName.trim()) return;
    try {
      const res = await fetch(`${API_URL}/master-categories/${masterId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ name: editName.trim() })
      });
      const data = await res.json();
      if (data.success) {
        showToast('✅ Name updated');
        setEditingMaster(null);
        await loadData();
      } else {
        showToast(`❌ ${data.error || 'Update failed'}`);
      }
    } catch (err) {
      showToast('❌ Network error');
    }
  };

  // Delete master
  const handleDelete = async (masterId, masterName) => {
    if (!window.confirm(`Delete "${masterName}"? All micro-categories will be unlinked.`)) return;
    try {
      const res = await fetch(`${API_URL}/master-categories/${masterId}`, {
        method: 'DELETE',
        headers
      });
      const data = await res.json();
      if (data.success) {
        showToast(`✅ Deleted "${masterName}"`);
        await loadData();
      } else {
        showToast(`❌ ${data.error || 'Delete failed'}`);
      }
    } catch (err) {
      showToast('❌ Network error');
    }
  };

  // Add custom cuisine
  const handleAddCuisine = async () => {
    if (!newCuisineName.trim()) return;
    try {
      const res = await fetch(`${API_URL}/master-categories`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ name: newCuisineName.trim() })
      });
      const data = await res.json();
      if (data.success) {
        showToast(`✅ Created "${newCuisineName.trim()}"`);
        setNewCuisineName('');
        setShowAddCuisine(false);
        await loadData();
      } else {
        showToast(`❌ ${data.error || 'Creation failed'}`);
      }
    } catch (err) {
      showToast('❌ Network error');
    }
  };

  const sorted = [...masterCategories].sort((a, b) => a.display_order - b.display_order);

  return (
    <div style={{
      maxWidth: '600px',
      margin: '0 auto',
      minHeight: '100vh',
      background: '#f9fafb',
      paddingBottom: '100px'
    }}>
      {/* Header */}
      <div style={{
        padding: '16px 20px',
        background: '#fff',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <button
          onClick={() => goTo('settings')}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            padding: '8px', borderRadius: '8px', color: '#374151',
            display: 'flex', alignItems: 'center'
          }}
        >
          <FaArrowLeft size={18} />
        </button>
        <div>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#111' }}>
            Category Management
          </h2>
          <p style={{ margin: 0, fontSize: '12px', color: '#6b7280' }}>
            Master Cuisines & Micro-Categories
          </p>
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: '16px 20px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: '#6b7280' }}>
            <div style={{
              width: '40px', height: '40px',
              border: '3px solid #e5e7eb', borderTop: '3px solid #000',
              borderRadius: '50%', animation: 'spin 0.8s linear infinite',
              margin: '0 auto 16px'
            }} />
            <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
            Loading categories...
          </div>
        ) : error ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: '#ef4444' }}>
            {error}
            <button onClick={loadData} style={{
              display: 'block', margin: '12px auto 0', padding: '8px 16px',
              background: '#000', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer'
            }}>Retry</button>
          </div>
        ) : masterCategories.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: '#6b7280' }}>
            <p style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>No master categories found</p>
            <p style={{ fontSize: '14px' }}>Categories will appear here after onboarding.</p>
          </div>
        ) : (
          <>
            {/* Warning banner */}
            <div style={{
              background: '#fffbeb',
              border: '1px solid #fbbf24',
              borderRadius: '10px',
              padding: '12px 16px',
              marginBottom: '16px',
              fontSize: '13px',
              color: '#92400e',
              lineHeight: '1.5'
            }}>
              ⚠️ <strong>Swapping categories</strong> triggers a background recalculation of historical reports. This may take a few seconds.
            </div>

            {/* Master group cards */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {sorted.map((master, index) => (
                <div key={master.master_category_id} style={{
                  background: '#fff',
                  borderRadius: '12px',
                  border: '1px solid #e5e7eb',
                  overflow: 'hidden',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
                }}>
                  {/* Master header */}
                  <div style={{
                    padding: '14px 16px',
                    background: '#f9fafb',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: '1px solid #e5e7eb'
                  }}>
                    <div style={{ flex: 1 }}>
                      {editingMaster === master.master_category_id ? (
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <input
                            type="text"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            autoFocus
                            style={{
                              padding: '6px 10px', border: '1px solid #d1d5db',
                              borderRadius: '6px', fontSize: '14px', fontWeight: '600', flex: 1
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleEditSave(master.master_category_id);
                              if (e.key === 'Escape') setEditingMaster(null);
                            }}
                          />
                          <button onClick={() => handleEditSave(master.master_category_id)} style={{
                            padding: '6px 10px', background: '#000', color: '#fff',
                            border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px'
                          }}>Save</button>
                          <button onClick={() => setEditingMaster(null)} style={{
                            padding: '6px 10px', background: '#f3f4f6', color: '#374151',
                            border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px'
                          }}>Cancel</button>
                        </div>
                      ) : (
                        <div>
                          <div style={{ fontWeight: '700', fontSize: '16px', color: '#111' }}>
                            {index + 1}. {master.name}
                          </div>
                          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                            Priority Index: #{index + 1} · {(master.microCategories || []).length} sub-categories
                          </div>
                        </div>
                      )}
                    </div>

                    {editingMaster !== master.master_category_id && (
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button onClick={() => handleReorder(master.master_category_id, 'up')}
                          disabled={index === 0}
                          style={{
                            padding: '6px', background: 'none', border: 'none', cursor: index === 0 ? 'default' : 'pointer',
                            color: index === 0 ? '#d1d5db' : '#6b7280', borderRadius: '4px'
                          }}>
                          <FaArrowUp size={12} />
                        </button>
                        <button onClick={() => handleReorder(master.master_category_id, 'down')}
                          disabled={index === sorted.length - 1}
                          style={{
                            padding: '6px', background: 'none', border: 'none',
                            cursor: index === sorted.length - 1 ? 'default' : 'pointer',
                            color: index === sorted.length - 1 ? '#d1d5db' : '#6b7280', borderRadius: '4px'
                          }}>
                          <FaArrowDown size={12} />
                        </button>
                        <button onClick={() => { setEditingMaster(master.master_category_id); setEditName(master.name); }}
                          style={{
                            padding: '6px', background: 'none', border: 'none', cursor: 'pointer',
                            color: '#6b7280', borderRadius: '4px'
                          }}>
                          <FaEdit size={12} />
                        </button>
                        <button onClick={() => handleDelete(master.master_category_id, master.name)}
                          style={{
                            padding: '6px', background: 'none', border: 'none', cursor: 'pointer',
                            color: '#ef4444', borderRadius: '4px'
                          }}>
                          <FaTrash size={12} />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Micro-categories list */}
                  <div style={{ padding: '12px 16px' }}>
                    {(master.microCategories || []).length === 0 ? (
                      <div style={{ color: '#9ca3af', fontSize: '13px', fontStyle: 'italic', padding: '8px 0' }}>
                        No categories assigned yet
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {(master.microCategories || []).map(micro => {
                          const swapKey = `${master.master_category_id}-${micro.category_id}`;
                          const isDropdownOpen = swapDropdownOpen === swapKey;
                          const otherMasters = sorted.filter(m => m.master_category_id !== master.master_category_id);

                          return (
                            <div key={micro.category_id} style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '8px 12px',
                              background: '#f9fafb',
                              borderRadius: '8px',
                              position: 'relative'
                            }}>
                              <span style={{ fontSize: '14px', fontWeight: '500', color: '#111' }}>
                                {micro.name}
                              </span>

                              <div style={{ position: 'relative' }}>
                                <button
                                  onClick={() => setSwapDropdownOpen(isDropdownOpen ? null : swapKey)}
                                  style={{
                                    padding: '4px 10px',
                                    background: '#fff',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    color: '#374151'
                                  }}
                                >
                                  <FaExchangeAlt size={10} />
                                  Swap
                                  <FaChevronDown size={8} style={{
                                    transform: isDropdownOpen ? 'rotate(180deg)' : 'none',
                                    transition: 'transform 0.15s'
                                  }} />
                                </button>

                                {isDropdownOpen && (
                                  <div style={{
                                    position: 'absolute',
                                    top: '100%',
                                    right: 0,
                                    marginTop: '4px',
                                    background: '#fff',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '8px',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                                    zIndex: 50,
                                    minWidth: '160px',
                                    overflow: 'hidden'
                                  }}>
                                    <div style={{
                                      padding: '8px 12px',
                                      fontSize: '11px',
                                      fontWeight: '600',
                                      color: '#6b7280',
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.5px',
                                      borderBottom: '1px solid #e5e7eb'
                                    }}>
                                      Move to
                                    </div>
                                    {otherMasters.map(other => (
                                      <button
                                        key={other.master_category_id}
                                        onClick={() => {
                                          setSwapConfirm({
                                            microId: micro.category_id,
                                            microName: micro.name,
                                            fromMasterName: master.name,
                                            toMasterId: other.master_category_id,
                                            toMasterName: other.name
                                          });
                                          setSwapDropdownOpen(null);
                                        }}
                                        style={{
                                          display: 'block',
                                          width: '100%',
                                          padding: '10px 12px',
                                          background: 'none',
                                          border: 'none',
                                          borderBottom: '1px solid #f3f4f6',
                                          cursor: 'pointer',
                                          fontSize: '13px',
                                          fontWeight: '500',
                                          color: '#111',
                                          textAlign: 'left'
                                        }}
                                      >
                                        {other.name}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* + Attach new micro-token */}
                  {unmappedMicros.length > 0 && (
                    <div style={{ padding: '0 16px 12px' }}>
                      {attachDropdownOpen === master.master_category_id ? (
                        <div className="cm-attach-dropdown">
                          <div style={{ padding: '8px 12px', fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #e5e7eb' }}>
                            Select category to attach
                          </div>
                          {unmappedMicros.map(micro => (
                            <button
                              key={micro.category_id}
                              className="cm-attach-option"
                              disabled={attachingTo === micro.category_id}
                              onClick={async () => {
                                setAttachingTo(micro.category_id);
                                try {
                                  const res = await fetch(`${API_URL}/master-categories/merge`, {
                                    method: 'PUT',
                                    headers,
                                    body: JSON.stringify({
                                      masterCategoryId: master.master_category_id,
                                      microCategoryIds: [micro.category_id]
                                    })
                                  });
                                  const data = await res.json();
                                  if (data.success) {
                                    showToast(`✅ Attached "${micro.name}" to ${master.name}`);
                                    await loadData();
                                  } else {
                                    showToast(`❌ ${data.error || 'Attach failed'}`);
                                  }
                                } catch (err) {
                                  showToast('❌ Network error');
                                } finally {
                                  setAttachingTo(null);
                                  setAttachDropdownOpen(null);
                                }
                              }}
                            >
                              {micro.name}
                            </button>
                          ))}
                          <button
                            onClick={() => setAttachDropdownOpen(null)}
                            style={{ display: 'block', width: '100%', padding: '8px 12px', background: '#f9fafb', border: 'none', cursor: 'pointer', fontSize: '12px', color: '#6b7280', fontWeight: '500' }}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          className="cm-attach-btn"
                          onClick={() => setAttachDropdownOpen(master.master_category_id)}
                        >
                          <FaPlus size={10} /> Attach new micro-token
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}

              {/* Add Custom Cuisine */}
              <div style={{
                border: '1px dashed #d1d5db',
                borderRadius: '12px',
                padding: '16px',
                background: '#fff'
              }}>
                {!showAddCuisine ? (
                  <button onClick={() => setShowAddCuisine(true)} style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: '8px',
                    color: '#6b7280', fontSize: '14px', fontWeight: '600', padding: 0, width: '100%'
                  }}>
                    <FaPlus size={12} /> Add Master Cuisine
                  </button>
                ) : (
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="text"
                      value={newCuisineName}
                      onChange={(e) => setNewCuisineName(e.target.value)}
                      placeholder="e.g. Tandoori Station"
                      autoFocus
                      style={{
                        flex: 1, padding: '10px 12px',
                        border: '1px solid #d1d5db', borderRadius: '8px',
                        fontSize: '14px', outline: 'none'
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleAddCuisine();
                        if (e.key === 'Escape') { setShowAddCuisine(false); setNewCuisineName(''); }
                      }}
                    />
                    <button onClick={handleAddCuisine} disabled={!newCuisineName.trim()} style={{
                      padding: '10px 16px', background: newCuisineName.trim() ? '#000' : '#d1d5db',
                      color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px',
                      fontWeight: '600', cursor: newCuisineName.trim() ? 'pointer' : 'not-allowed'
                    }}>Add</button>
                    <button onClick={() => { setShowAddCuisine(false); setNewCuisineName(''); }} style={{
                      padding: '10px', background: '#f3f4f6', border: 'none', borderRadius: '8px',
                      cursor: 'pointer', color: '#374151'
                    }}><FaTimes size={14} /></button>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Swap Confirmation Modal */}
      {swapConfirm && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 200, padding: '20px'
        }}>
          <div style={{
            background: '#fff', borderRadius: '12px', maxWidth: '400px',
            width: '100%', padding: '24px', textAlign: 'center',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <div style={{ fontSize: '18px', fontWeight: '700', color: '#111', marginBottom: '12px' }}>
              Confirm Category Swap
            </div>
            <div style={{ fontSize: '14px', color: '#6b7280', lineHeight: '1.6', marginBottom: '8px' }}>
              Moving <strong>"{swapConfirm.microName}"</strong> from <strong>{swapConfirm.fromMasterName}</strong> to <strong>{swapConfirm.toMasterName}</strong>.
            </div>
            <div style={{
              background: '#fffbeb', border: '1px solid #fbbf24', borderRadius: '8px',
              padding: '10px 14px', fontSize: '12px', color: '#92400e', marginBottom: '20px', lineHeight: '1.5'
            }}>
              ⚠️ This will recalculate historical reports. The process may take a few seconds.
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setSwapConfirm(null)}
                disabled={swapping}
                style={{
                  flex: 1, padding: '12px', background: '#f3f4f6', color: '#374151',
                  border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer'
                }}
              >Cancel</button>
              <button
                onClick={() => handleSwap(swapConfirm.microId, swapConfirm.toMasterId, swapConfirm.microName, swapConfirm.toMasterName)}
                disabled={swapping}
                style={{
                  flex: 1, padding: '12px', background: '#000', color: '#fff',
                  border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600',
                  cursor: swapping ? 'wait' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                }}
              >
                {swapping ? (
                  <>
                    <div style={{
                      width: '14px', height: '14px', border: '2px solid rgba(255,255,255,0.3)',
                      borderTop: '2px solid #fff', borderRadius: '50%',
                      animation: 'spin 0.6s linear infinite'
                    }} />
                    Swapping...
                  </>
                ) : 'Confirm Swap'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast notification */}
      {toast && (
        <div style={{
          position: 'fixed',
          bottom: '100px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: '#1f2937',
          color: '#fff',
          padding: '12px 24px',
          borderRadius: '10px',
          fontSize: '14px',
          fontWeight: '500',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          zIndex: 999,
          maxWidth: '90vw',
          textAlign: 'center',
          animation: 'fadeIn 0.2s ease'
        }}>
          <style>{`@keyframes fadeIn { from { opacity: 0; transform: translateX(-50%) translateY(10px); } to { opacity: 1; transform: translateX(-50%) translateY(0); } }`}</style>
          {toast}
        </div>
      )}
    </div>
  );
};

export default CategoryMaintenancePage;
